import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Radio, RadioGroup } from '@fluentui/react-components';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  Area,
  EntityType,
  IAnalyticsV2Filters,
  OS,
  Platform
} from '@tdp/modules/apps';

import { useFilterFormattedString } from './utils';

interface FilterRadio { filterType: EntityType | Platform | OS | Area, label: string }

export const FilterDrawerCheckBoxes: React.FC<{
  selectedFilters: IAnalyticsV2Filters,
  setSelectedFilters: React.Dispatch<React.SetStateAction<IAnalyticsV2Filters>>
}> = ({
  selectedFilters,
  setSelectedFilters
}) => {
    const intl = useIntl();
    const filterDrawerFormattedStrings = useFilterFormattedString(intl);

    const getRadioButton = (button: FilterRadio) => {
      return <Radio value={button.filterType} label={button.label} />
    }

    const entity: FilterRadio[] = [
      { filterType: EntityType.User, label: filterDrawerFormattedStrings.analyticsFilterAggTypeUserLabel },
      { filterType: EntityType.Tenant, label: filterDrawerFormattedStrings.analyticsFilterAggTypeTenantLabel }
    ]

    const platform: FilterRadio[] = [
      { filterType: Platform.AllUp, label: filterDrawerFormattedStrings.analyticsFilterAllLabel },
      { filterType: Platform.Desktop, label: filterDrawerFormattedStrings.analyticsFilterPlatformDesktopLabel },
      { filterType: Platform.Mobile, label: filterDrawerFormattedStrings.analyticsFilterPlatformMobileLabel },
      { filterType: Platform.Web, label: filterDrawerFormattedStrings.analyticsFilterPlatformWebLabel },
      { filterType: Platform.Universal, label: filterDrawerFormattedStrings.analyticsFilterPlatformUniversalLabel },
      { filterType: Platform.Unknown, label: filterDrawerFormattedStrings.analyticsFilterPlatformUnknownLabel }
    ]

    const os: FilterRadio[] = [
      { filterType: OS.AllUp, label: filterDrawerFormattedStrings.analyticsFilterAllLabel },
      { filterType: OS.Windows, label: filterDrawerFormattedStrings.analyticsFilterOperatingSystemWindowsLabel },
      { filterType: OS.Mac, label: filterDrawerFormattedStrings.analyticsFilterOperatingSystemMacLabel },
      { filterType: OS.iOS, label: filterDrawerFormattedStrings.analyticsFilterOperatingSystemiOSLabel },
      { filterType: OS.Android, label: filterDrawerFormattedStrings.analyticsFilterOperatingSystemAndroidLabel },
      { filterType: OS.ChromeOS, label: filterDrawerFormattedStrings.analyticsFilterOperatingSystemChromeOSLabel },
      { filterType: OS.Other, label: filterDrawerFormattedStrings.analyticsFilterOtherLabel },
      { filterType: OS.Unknown, label: filterDrawerFormattedStrings.analyticsFilterUnknownLabel }
    ]

    const area: FilterRadio[] = [
      { filterType: Area.AllUp, label: filterDrawerFormattedStrings.analyticsFilterAllLabel },
      { filterType: Area.UnitedStates, label: filterDrawerFormattedStrings.analyticsFilterAreaUnitedStatesLabel },
      { filterType: Area.WesternEurope, label: filterDrawerFormattedStrings.analyticsFilterAreaWesternEuropeLabel },
      { filterType: Area.LatinAmerica, label: filterDrawerFormattedStrings.analyticsFilterAreaLatinAmericaLabel },
      { filterType: Area.Japan, label: filterDrawerFormattedStrings.analyticsFilterAreaJapanLabel },
      { filterType: Area.India, label: filterDrawerFormattedStrings.analyticsFilterAreaIndiaLabel },
      { filterType: Area.Canada, label: filterDrawerFormattedStrings.analyticsFilterAreaCanadaLabel },
      { filterType: Area.AustraliaAndNewZealand, label: filterDrawerFormattedStrings.analyticsFilterAreaAustraliaLabel },
      { filterType: Area.UnitedKingdom, label: filterDrawerFormattedStrings.analyticsFilterAreaUnitedKingdomLabel },
      { filterType: Area.France, label: filterDrawerFormattedStrings.analyticsFilterAreaFranceLabel },
      { filterType: Area.AsiaPacific, label: filterDrawerFormattedStrings.analyticsFilterAreaAsiaPacificLabel },
      { filterType: Area.Germany, label: filterDrawerFormattedStrings.analyticsFilterAreaGermanyLabel },
      { filterType: Area.Switzerland, label: filterDrawerFormattedStrings.analyticsFilterAreaSwitzerlandLabel },
      { filterType: Area.Korea, label: filterDrawerFormattedStrings.analyticsFilterAreaKoreaLabel },
      { filterType: Area.Netherlands, label: filterDrawerFormattedStrings.analyticsFilterAreaNetherlandsLabel },
      { filterType: Area.CentralEuropeAndMiddleEastAndAfrica, label: filterDrawerFormattedStrings.analyticsFilterAreaCentralAndEasternEuropeLabel }
    ]

    return <Accordion collapsible>

      <AccordionItem value={1}>
        <AccordionHeader>{intl.formatMessage({ defaultMessage: "Aggregation type", description: "Label for the aggregation type (user or tenant) in the Analytics filter dialog" })}</AccordionHeader>
        <AccordionPanel>
          <RadioGroup
            defaultValue={selectedFilters.entityType}
            onChange={(_, data) => { setSelectedFilters({ ...selectedFilters, entityType: data.value as EntityType }); }}
          >
            {entity.map(row => getRadioButton(row))}
          </RadioGroup>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem value={2}>
        <AccordionHeader>{intl.formatMessage({ defaultMessage: "Platform", description: "Label for the platform in the Analytics filter dialog" })}</AccordionHeader>
        <AccordionPanel>
          <RadioGroup
            defaultValue={selectedFilters.platform}
            onChange={(_, data) => { setSelectedFilters({ ...selectedFilters, platform: data.value as Platform }); }}
          >
            {platform.map(row => getRadioButton(row))}
          </RadioGroup>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem value={3}>
        <AccordionHeader>{intl.formatMessage({ defaultMessage: "Operating system", description: "Label for the operating system on the Analytics filter dialog" })}</AccordionHeader>
        <AccordionPanel>
          <RadioGroup
            defaultValue={selectedFilters.os}
            onChange={(_, data) => { setSelectedFilters({ ...selectedFilters, os: data.value as OS }); }}
          >
            {os.map(row => getRadioButton(row))}
          </RadioGroup>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem value={4}>
        <AccordionHeader>{intl.formatMessage({ defaultMessage: "Area", description: "Label for the area in the Analytics filter dialog" })}</AccordionHeader>
        <AccordionPanel>
          <RadioGroup
            defaultValue={selectedFilters.area}
            onChange={(_, data) => { setSelectedFilters({ ...selectedFilters, area: data.value as Area }); }}
          >
            {area.map(row => getRadioButton(row))}
          </RadioGroup>
        </AccordionPanel>
      </AccordionItem>


    </Accordion>
  }